import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import BomImport from './BomImport';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#f472b6';

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
const SUPPLIER_ROLES = ['gpv','ubiqconn','acmepoint','hk_ringer'];

export default function BomUpload() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const isStaff = ['admin','quality-manager','production-engineer','service'].includes(role);
  const isSupplier = SUPPLIER_ROLES.includes(String(role||'').replace(/^test-/,''));
  const canEdit = isStaff || isSupplier;

  const [docNum, setDocNum] = useState('');
  const [name, setName] = useState('');
  const [productFamily, setProductFamily] = useState('');
  const [productNumbersText, setProductNumbersText] = useState('');
  const [items, setItems] = useState([]);
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canEdit) { navigate('/quality/boms'); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=BOM-NIN-`, {
        headers:{ Authorization:`Bearer ${token}` },
      }).then(r=>r.json()).then(d=>{ if (d.ok) setDocNum(d.documentNumber); })
    );
  }, []);// eslint-disable-line

  const save = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (items.length === 0) { setError('Add at least one item (paste rows or upload a CSV)'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const productNumbers = productNumbersText.split(/[\n,]/).map(s=>s.trim()).filter(Boolean);
      const res = await window.fetch(`${API_BASE}/quality/boms`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({
          documentNumber: docNum, name, productFamily, productNumbers, items,
          sourceFileName: fileName, rawHeaders: [],
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error||'Failed to save BOM');
      navigate(`/quality/boms/${data.bom._id}`);
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };

  return (
    <div style={{ maxWidth:880 }}>
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:24 }}>
        <button onClick={()=>navigate('/quality/boms')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>New BOM</h2>
      </div>

      <div style={{ background:ACCENT+'12',border:`1px solid ${ACCENT}33`,borderRadius:8,
        padding:'10px 14px',fontSize:13,color:'var(--ni-text-muted)',marginBottom:18 }}>
        Saved as <strong style={{ color:'var(--ni-text)' }}>Draft (Rev. A1)</strong>. Submit it for review when ready —
        NI releases it. Every later change opens a new revision.
      </div>

      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
        <div><label style={labelS}>Document Number</label>
          <input value={docNum} readOnly style={{ ...inp,opacity:0.6,cursor:'not-allowed' }} /></div>
        <div><label style={labelS}>Product Family</label>
          <input value={productFamily} onChange={e=>setProductFamily(e.target.value)}
            placeholder="e.g. MON / DIU / ELU" style={inp} /></div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={labelS}>Name *</label>
        <input value={name} onChange={e=>setName(e.target.value)}
          placeholder="e.g. Monitor Main Board BOM" style={inp} />
      </div>

      <div style={{ marginBottom:24 }}>
        <label style={labelS}>Product Numbers (one per line or comma-separated)</label>
        <textarea value={productNumbersText} onChange={e=>setProductNumbersText(e.target.value)}
          rows={2} placeholder="MON-1000&#10;MON-1001" style={{ ...inp,resize:'vertical' }} />
      </div>

      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
        borderRadius:10,padding:18,marginBottom:20 }}>
        <div style={{ ...labelS,marginBottom:14 }}>Import items</div>
        <BomImport onItems={setItems} onFileName={setFileName} />
      </div>

      {error && <div style={{ color:'#EC2124',fontSize:13,marginBottom:12 }}>{error}</div>}
      <div style={{ display:'flex',gap:10 }}>
        <button onClick={save} disabled={saving}
          style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,
            padding:'10px 24px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
          {saving?'Saving…':`Save BOM (${items.length} item${items.length!==1?'s':''})`}
        </button>
        <button onClick={()=>navigate('/quality/boms')}
          style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
            borderRadius:6,padding:'10px 20px',fontSize:13,cursor:'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}
